import { useCallback, useEffect, useState } from 'react'

import type { Warehouse } from 'repositories/BerthApi/getWarehouses/type'
import { useWarehousesQuery } from 'hooks/useWarehousesQuery'
import loginStorage from 'utils/loginStorage'

type SelectOption = {
  value: number
  label: string
  'data-dg-id'?: string
}

export type UseWarehouseSelectionReturn = {
  warehouseList: Warehouse[]
  warehouseId: number | null
  tenantId: number | null
  warehouseOptions: SelectOption[]
  tenantOptions: SelectOption[]
  changeWarehouseId: (id: number) => void
  changeTenantId: (id: number) => void
}

export const useWarehouseSelection = (): UseWarehouseSelectionReturn => {
  // 倉庫一覧はサーバーステート → 共通 Query hook（詳細ヘッダーと queryKey / キャッシュを共有）
  const { warehouseList } = useWarehousesQuery()

  // 選択中の倉庫 / テナントは UI ステート
  const [warehouseId, setWarehouseId] = useState<number | null>(null)
  const [tenantId, setTenantId] = useState<number | null>(null)
  const [initialized, setInitialized] = useState(false)

  // 倉庫一覧が取得できたら初期選択を確定する（セッション復元 → なければ先頭）。一度だけ。
  useEffect(() => {
    if (initialized || warehouseList.length === 0) return

    const session = loginStorage.getWarehouseSession()
    const sessionWarehouseId = typeof session.warehouseId === 'number' ? session.warehouseId : null
    const sessionTenantId = typeof session.tenantId === 'number' ? session.tenantId : null

    const targetWarehouse =
      warehouseList.find((w) => w.id === sessionWarehouseId) ?? warehouseList[0] ?? null
    if (!targetWarehouse) return

    const resolvedWarehouseId = targetWarehouse.id
    const targetTenant =
      targetWarehouse.tenants.find((t) => t.id === sessionTenantId) ??
      targetWarehouse.tenants[0] ??
      null
    const resolvedTenantId = targetTenant?.id ?? null

    setWarehouseId(resolvedWarehouseId)
    setTenantId(resolvedTenantId)
    loginStorage.setWarehouseSession({
      warehouseId: resolvedWarehouseId,
      tenantId: resolvedTenantId,
    })
    setInitialized(true)
  }, [warehouseList, initialized])

  const changeWarehouseId = useCallback(
    (id: number) => {
      const warehouse = warehouseList.find((w) => w.id === id)
      if (!warehouse) return

      const firstTenantId = warehouse.tenants[0]?.id ?? null
      setWarehouseId(id)
      setTenantId(firstTenantId)
      loginStorage.setWarehouseSession({ warehouseId: id, tenantId: firstTenantId })
    },
    [warehouseList]
  )

  const changeTenantId = useCallback(
    (id: number) => {
      setTenantId(id)
      loginStorage.setWarehouseSession({ warehouseId: warehouseId, tenantId: id })
    },
    [warehouseId]
  )

  const warehouseOptions: SelectOption[] = warehouseList.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

  const tenantOptions: SelectOption[] = (() => {
    const warehouse = warehouseList.find((w) => w.id === warehouseId)
    if (!warehouse) return []
    return warehouse.tenants.map(({ id, name }) => ({
      value: id,
      label: name,
      'data-dg-id': `delivery-performance-tenant-select-option-${id}`,
    }))
  })()

  return {
    warehouseList,
    warehouseId,
    tenantId,
    warehouseOptions,
    tenantOptions,
    changeWarehouseId,
    changeTenantId,
  }
}
